import type {
  SyncConfiguration,
  FieldMapping,
} from "../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";

export type SyncConfigurationLike =
  | SyncConfiguration[]
  | { data?: SyncConfiguration[] }
  | null
  | undefined;

export type FieldMappingLike =
  | FieldMapping[]
  | { data?: FieldMapping[] }
  | null
  | undefined;

export type UiObjectSlug = "accounts" | "contacts" | "companies" | "deals" | "notes" | "tasks" | "emails";
export type NormalizedSyncDirection = "write_in" | "write_out" | "";

export const toUiSlug = (slug?: string | null): string => {
  if (!slug) return "";
  if (slug === "users") return "contacts";
  if (slug === "conversation_messages") return "emails";
  return slug;
};

export const toBackendSlug = (slug: string): string => (
  slug === "contacts"
    ? "users"
    : slug === "emails"
      ? "conversation_messages"
      : slug
);

export const normalizeSyncDirection = (direction?: string | null): NormalizedSyncDirection => {
  const raw = String(direction || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "write_in" || raw.endsWith(".write_in")) return "write_in";
  if (raw === "write_out" || raw.endsWith(".write_out")) return "write_out";
  return "";
};

export const extractSyncConfigurations = (
  payload: SyncConfigurationLike,
): SyncConfiguration[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const extractFieldMappings = (payload: FieldMappingLike): FieldMapping[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export const normalizeProviderSyncConfigurations = ({
  payload,
  integrationId,
}: {
  payload: SyncConfigurationLike;
  integrationId?: string;
}): SyncConfiguration[] => {
  const syncs = extractSyncConfigurations(payload);
  if (!integrationId) return [];
  return syncs.filter((sync) => sync?.integration_id === integrationId);
};

export const isCoreCrmObject = (slug: string): slug is UiObjectSlug => (
  slug === "accounts" || slug === "contacts" || slug === "companies"
);

export const asRetriableError = (error: unknown): { message: string; retriable: boolean } => {
  const maybeStatus = (error as any)?.response?.status as number | undefined;
  const message = (error as any)?.message || "Unknown error";
  // Retry-safe for transient server/network-ish failures.
  const retriable = !maybeStatus || maybeStatus >= 500 || maybeStatus === 429;
  return { message, retriable };
};
